import ScreenWrapper from "@/components/ScreenWrapper"
import { useAuthStore } from "@/stores/useAuthStore"
import { QueryClient } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import {
  createRootRouteWithContext,
  Outlet,
  redirect,
  useRouter,
} from "@tanstack/react-router"
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools"
import { ToastContainer } from "react-toastify"
import {
  DEFAULT_STALE_TIME,
  ErrorBoundary,
  NotFoundScreen,
} from "toptaal-shared"
import { userQueryKeys } from "@/api/query-keys/users"
import { getMe } from "@/api/services/user"
import { SimpleUser } from "@/api/types/user"
import { isMissingTeacherInfoUtil } from "@/utils/isMissingTeacherInfoUtil"

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient
}>()({
  beforeLoad: async ({ location, context }) => {
    const { getToken, getRefreshToken } = useAuthStore.getState()
    const token = getToken()
    const refreshToken = getRefreshToken()

    const openPaths = [
      "/onboarding",
      "/auth",
      "/login",
      "/freelancer_omzetten",
      "/auth/confirm_account/$token",
    ]

    if (openPaths.some((path) => location.pathname.startsWith(path))) {
      return
    }

    if (!refreshToken && !token) {
      throw redirect({ to: "/login", replace: true })
    }

    if (token || (refreshToken && location.pathname.includes("/onboarding"))) {
      await context.queryClient.prefetchQuery({
        queryKey: userQueryKeys.current(),
        queryFn: getMe,
        staleTime: DEFAULT_STALE_TIME,
      })

      const userData = context.queryClient.getQueryData<{
        data: SimpleUser
      }>(userQueryKeys.current())

      const isMissingTeacherInfo = isMissingTeacherInfoUtil(userData?.data)

      if (userData?.data) {
        if (
          isMissingTeacherInfo &&
          !location.pathname.startsWith("/onboarding")
        ) {
          throw redirect({ to: "/onboarding/docent-onboarding", replace: true })
        }
      }
    }
  },
  notFoundComponent: () => (
    <ScreenWrapper className="screen-height-minus-navbar flex items-center justify-center !px-4 md:!px-8 !max-w-none bg-primary-light-blue relative overflow-hidden">
      <NotFoundScreen />
    </ScreenWrapper>
  ),
  component: RootComponent,
})

function RootComponent() {
  const router = useRouter()

  const content = (
    <>
      <Outlet />
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        theme="light"
        limit={3}
      />
      <TanStackRouterDevtools position="bottom-left" />
      <ReactQueryDevtools initialIsOpen={false} />
    </>
  )

  if (!import.meta.env.PROD) {
    return content
  }

  return (
    <ErrorBoundary
      navigateBack={() => {
        router.history.back()
      }}
      ScreenWrapper={ScreenWrapper}
      navigateHome={() =>
        router.navigate({
          to: "/",
          replace: true,
        })
      }
    >
      {content}
    </ErrorBoundary>
  )
}
